import type { ShopCurrency } from "@/shared/lib/currency";
import {
  bigserial,
  boolean,
  index,
  jsonb,
  numeric,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
} from "drizzle-orm/pg-core";
import { user } from "./auth";
import { mediaAssets } from "./catalog";

// =====================================================
// WALLET / LEDGER / SETTLEMENT — Single-tenant
// =====================================================

export const wallets = pgTable(
  "wallets",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    ownerType: varchar("owner_type", { length: 50 })
      .$type<"customer" | "shop" | "mechanic" | "platform">()
      .notNull(), // customer | shop | mechanic | platform
    ownerId: text("owner_id"), // user_id (text) | shop_id | mechanic_id | null for platform
    currency: varchar("currency", { length: 10 })
      .$type<ShopCurrency>()
      .default("LAK")
      .notNull(), // LAK | THB | USD...
    status: varchar("status", { length: 50 })
      .$type<"active" | "frozen" | "closed">()
      .default("active")
      .notNull(), // active | frozen | closed
    kycTier: varchar("kyc_tier", { length: 20 })
      .$type<"unverified" | "basic" | "verified" | "premium">()
      .default("unverified")
      .notNull(),
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
    }).notNull(),
  },
  (t) => [
    uniqueIndex("wallets_owner_currency_unique").on(
      t.ownerType,
      t.ownerId,
      t.currency,
    ),
    index("wallets_currency").on(t.currency),
    index("wallets_kyc_tier").on(t.kycTier),
  ],
);

export const walletTransactions = pgTable(
  "wallet_transactions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    type: varchar("type", { length: 50 })
      .$type<
        | "topup"
        | "pay_order"
        | "pay_pos"
        | "pay_job"
        | "refund"
        | "payout"
        | "fee"
        | "adjust"
      >()
      .notNull(), // topup | pay_order | pay_pos (POS wallet) | pay_job | refund | payout | fee | adjust
    status: varchar("status", { length: 50 })
      .$type<"pending" | "posted" | "failed" | "reversed">()
      .default("pending")
      .notNull(), // pending | posted | failed | reversed
    amount: numeric("amount", {
      precision: 14,
      scale: 2,
      mode: "number",
    }).notNull(),
    currency: varchar("currency", { length: 10 })
      .$type<ShopCurrency>()
      .default("LAK")
      .notNull(),
    referenceType: varchar("reference_type", { length: 50 }).$type<
      "order" | "job" | "pos_sale" | "manual"
    >(), // order | job | pos_sale | manual
    referenceId: uuid("reference_id"),
    note: text("note"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
    postedAt: timestamp("posted_at", {
      withTimezone: true,
    }),
  },
  (t) => [
    index("wallet_transactions_status_created").on(t.status, t.createdAt),
    index("wallet_transactions_reference").on(t.referenceType, t.referenceId),
  ],
);

export const walletLedgerEntries = pgTable(
  "wallet_ledger_entries",
  {
    id: bigserial("id", { mode: "number" }).primaryKey(),
    walletId: uuid("wallet_id")
      .notNull()
      .references(() => wallets.id),
    txId: uuid("tx_id")
      .notNull()
      .references(() => walletTransactions.id),
    direction: varchar("direction", { length: 10 })
      .$type<"debit" | "credit">()
      .notNull(), // debit | credit
    amount: numeric("amount", {
      precision: 14,
      scale: 2,
      mode: "number",
    }).notNull(),
    balanceAfter: numeric("balance_after", {
      precision: 14,
      scale: 2,
      mode: "number",
    }), // optional: for fast query
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
  },
  (t) => [
    index("wallet_ledger_entries_wallet_created").on(t.walletId, t.createdAt),
    index("wallet_ledger_entries_tx").on(t.txId),
  ],
);

// Optional: unify all payments under one model
export const paymentIntents = pgTable(
  "payment_intents",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    payerType: varchar("payer_type", { length: 50 })
      .$type<"customer" | "shop" | "mechanic">()
      .notNull(), // customer | shop | mechanic
    payerId: text("payer_id").notNull(), // user_id | shop_id | mechanic_id
    payeeType: varchar("payee_type", { length: 50 })
      .$type<"platform" | "shop" | "mechanic">()
      .notNull(), // platform | shop | mechanic
    payeeId: text("payee_id").notNull(), // shop_id | mechanic_id | platform
    method: varchar("method", { length: 50 })
      .$type<"cash" | "transfer" | "wallet" | "provider">()
      .notNull(), // cash | transfer | wallet | provider
    status: varchar("status", { length: 50 })
      .$type<"created" | "authorized" | "captured" | "failed" | "refunded">()
      .default("created")
      .notNull(), // created | authorized | captured | failed | refunded
    amount: numeric("amount", {
      precision: 14,
      scale: 2,
      mode: "number",
    }).notNull(),
    currency: varchar("currency", { length: 10 })
      .$type<ShopCurrency>()
      .default("LAK")
      .notNull(),
    referenceType: varchar("reference_type", { length: 50 }).$type<
      "order" | "pos_sale" | "job"
    >(), // order | pos_sale | job
    referenceId: uuid("reference_id"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
    }).notNull(),
  },
  (t) => [
    index("payment_intents_reference").on(t.referenceType, t.referenceId),
    index("payment_intents_status_created").on(t.status, t.createdAt),
  ],
);

export const topupRequests = pgTable(
  "topup_requests",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    walletId: uuid("wallet_id")
      .notNull()
      .references(() => wallets.id),
    amount: numeric("amount", {
      precision: 14,
      scale: 2,
      mode: "number",
    }).notNull(),
    currency: varchar("currency", { length: 10 })
      .$type<ShopCurrency>()
      .default("LAK")
      .notNull(),
    method: varchar("method", { length: 50 })
      .$type<"transfer" | "provider">()
      .notNull(), // transfer | provider
    status: varchar("status", { length: 50 })
      .$type<"submitted" | "approved" | "rejected" | "posted">()
      .default("submitted")
      .notNull(), // submitted | approved | rejected | posted
    slipMediaId: uuid("slip_media_id").references(() => mediaAssets.id), // ref: media_assets.id
    approvedBy: text("approved_by").references(() => user.id),
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
    approvedAt: timestamp("approved_at", {
      withTimezone: true,
    }),
    rejectReason: text("reject_reason"),
    transferReference: text("transfer_reference"),
  },
  (t) => [
    index("topup_requests_status_created").on(t.status, t.createdAt),
    index("topup_requests_wallet_created").on(t.walletId, t.createdAt),
  ],
);

export const payoutRequests = pgTable(
  "payout_requests",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    walletId: uuid("wallet_id")
      .notNull()
      .references(() => wallets.id),
    amount: numeric("amount", {
      precision: 14,
      scale: 2,
      mode: "number",
    }).notNull(),
    currency: varchar("currency", { length: 10 })
      .$type<ShopCurrency>()
      .default("LAK")
      .notNull(),
    status: varchar("status", { length: 50 })
      .$type<
        "requested" | "approved" | "processing" | "paid" | "failed" | "rejected"
      >()
      .default("requested")
      .notNull(), // requested | approved | processing | paid | failed | rejected
    payoutMethod: varchar("payout_method", { length: 50 })
      .$type<"bank_transfer" | "cash">()
      .notNull(), // bank_transfer | cash
    payoutDestination: jsonb("payout_destination"), // bank account, name, etc.
    requestedBy: text("requested_by").references(() => user.id),
    approvedBy: text("approved_by").references(() => user.id),
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
    }).notNull(),
  },
  (t) => [
    index("payout_requests_status_created").on(t.status, t.createdAt),
    index("payout_requests_wallet_created").on(t.walletId, t.createdAt),
  ],
);

export const kycVerificationRequests = pgTable(
  "kyc_verification_requests",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: text("user_id")
      .notNull()
      .references(() => user.id),
    walletId: uuid("wallet_id")
      .notNull()
      .references(() => wallets.id),
    requestedTier: varchar("requested_tier", { length: 20 })
      .$type<"basic" | "verified">()
      .notNull(),
    status: varchar("status", { length: 20 })
      .$type<"submitted" | "under_review" | "approved" | "rejected">()
      .default("submitted")
      .notNull(),
    fullName: text("full_name"),
    idNumber: text("id_number"),
    idDocumentMediaId: uuid("id_document_media_id").references(
      () => mediaAssets.id,
    ),
    selfieMediaId: uuid("selfie_media_id").references(() => mediaAssets.id),
    rejectReason: text("reject_reason"),
    reviewedBy: text("reviewed_by").references(() => user.id),
    reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
    }).notNull(),
  },
  (t) => [
    index("kyc_requests_status_created").on(t.status, t.createdAt),
    index("kyc_requests_user").on(t.userId),
  ],
);

export const kycTierLimits = pgTable(
  "kyc_tier_limits",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tier: varchar("tier", { length: 50 })
      .$type<"unverified" | "basic" | "verified" | "premium">()
      .notNull(),
    dailyWithdraw: numeric("daily_withdraw", {
      precision: 14,
      scale: 2,
      mode: "number",
    }).notNull(),
    monthlyWithdraw: numeric("monthly_withdraw", {
      precision: 14,
      scale: 2,
      mode: "number",
    }).notNull(),
    dailyTopup: numeric("daily_topup", {
      precision: 14,
      scale: 2,
      mode: "number",
    }).notNull(),
    currency: varchar("currency", { length: 10 })
      .$type<ShopCurrency>()
      .default("LAK")
      .notNull(),
    description: text("description"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
    }).notNull(),
  },
  (t) => [uniqueIndex("kyc_tier_limits_tier_currency").on(t.tier, t.currency)],
);

export const suspiciousFlags = pgTable(
  "suspicious_flags",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    walletId: uuid("wallet_id")
      .notNull()
      .references(() => wallets.id),
    txId: uuid("tx_id").references(() => walletTransactions.id),
    reason: text("reason").notNull(),
    severity: varchar("severity", { length: 20 })
      .$type<"low" | "medium" | "high" | "critical">()
      .default("medium")
      .notNull(),
    status: varchar("status", { length: 20 })
      .$type<"pending" | "reviewed" | "escalated" | "cleared">()
      .default("pending")
      .notNull(),
    reviewedBy: text("reviewed_by").references(() => user.id),
    reviewNote: text("review_note"),
    reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
  },
  (t) => [
    index("suspicious_flags_status").on(t.status, t.createdAt),
    index("suspicious_flags_wallet").on(t.walletId),
    index("suspicious_flags_severity").on(t.severity),
  ],
);

export const walletFeeConfigs = pgTable(
  "wallet_fee_configs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    feeType: varchar("fee_type", { length: 50 })
      .$type<"marketplace_commission" | "withdraw_fee" | "top_up_fee">()
      .notNull(),
    feeMode: varchar("fee_mode", { length: 20 })
      .$type<"percent" | "flat">()
      .notNull(),
    value: numeric("value", {
      precision: 14,
      scale: 4,
      mode: "number",
    }).notNull(),
    currency: varchar("currency", { length: 10 })
      .$type<ShopCurrency>()
      .default("LAK")
      .notNull(),
    minAmount: numeric("min_amount", {
      precision: 14,
      scale: 2,
      mode: "number",
    }),
    maxAmount: numeric("max_amount", {
      precision: 14,
      scale: 2,
      mode: "number",
    }),
    description: text("description"),
    isActive: boolean("is_active").default(true).notNull(),
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
    }).notNull(),
  },
  (t) => [index("wallet_fee_configs_type_active").on(t.feeType, t.isActive)],
);

// =====================================================
// RECONCILIATION
// =====================================================

export const reconciliationRecords = pgTable(
  "reconciliation_records",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    sourceType: varchar("source_type", { length: 50 })
      .$type<"bank_statement" | "payment_provider" | "manual">()
      .notNull(),
    sourceRef: varchar("source_ref", { length: 255 }).notNull(),
    amount: numeric("amount", {
      precision: 14,
      scale: 2,
      mode: "number",
    }).notNull(),
    currency: varchar("currency", { length: 10 })
      .$type<ShopCurrency>()
      .default("LAK")
      .notNull(),
    txDate: timestamp("tx_date", { withTimezone: true }).notNull(),
    description: text("description"),
    status: varchar("status", { length: 20 })
      .$type<"pending" | "matched" | "unmatched" | "discrepancy" | "resolved">()
      .default("pending")
      .notNull(),
    matchedTransactionId: uuid("matched_transaction_id").references(
      () => walletTransactions.id,
    ),
    matchedPayoutId: uuid("matched_payout_id").references(
      () => payoutRequests.id,
    ),
    discrepancyAmount: numeric("discrepancy_amount", {
      precision: 14,
      scale: 2,
      mode: "number",
    }),
    notes: text("notes"),
    reconciledBy: text("reconciled_by").references(() => user.id),
    reconciledAt: timestamp("reconciled_at", { withTimezone: true }),
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
    }).notNull(),
  },
  (t) => [
    index("recon_records_status").on(t.status, t.createdAt),
    index("recon_records_source_type").on(t.sourceType),
    index("recon_records_matched_tx").on(t.matchedTransactionId),
  ],
);
